import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

interface Device {
  device_id: string;
  name: string;
  os: string;
  status: string;
  compliance_status: string;
  agent_id?: string;
  last_seen?: string;
}

const MacOSManagement: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);

  useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    try {
      const response = await apiService.getDevices();
      const macDevices = response.filter((device: Device) => {
        const os = device.os?.toLowerCase() || '';
        return os.includes('mac') || os.includes('darwin') || os.includes('os x');
      });
      setDevices(macDevices);
    } catch (err) {
      console.error('Error loading devices:', err);
    }
  };

  const toggleDeviceSelection = (deviceId: string) => {
    setSelectedDevices(prev =>
      prev.includes(deviceId)
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px', color: '#1f2937' }}>
          macOS Management
        </h1>
        <p style={{ color: '#6b7280', fontSize: '16px' }}>
          Manage Mac devices running macOS
        </p>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 className="card-title">Devices ({devices.length})</h2>
          <button className="action-btn">+ Enroll Device</button>
        </div>

        {devices.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
            <svg width="64" height="64" fill="#000000" viewBox="0 0 24 24" style={{ margin: '0 auto 16px', opacity: 0.5 }}>
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.26-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            <p style={{ fontSize: '18px', fontWeight: '500', marginBottom: '8px' }}>No macOS devices enrolled</p>
            <p>Enroll macOS devices to start managing them</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="device-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedDevices.length === devices.length && devices.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDevices(devices.map(d => d.device_id));
                        } else {
                          setSelectedDevices([]);
                        }
                      }}
                    />
                  </th>
                  <th>Device Name</th>
                  <th>macOS Version</th>
                  <th>Status</th>
                  <th>Compliance</th>
                  <th>Last Seen</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((device) => (
                  <tr key={device.device_id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedDevices.includes(device.device_id)}
                        onChange={() => toggleDeviceSelection(device.device_id)}
                      />
                    </td>
                    <td style={{ fontWeight: '500', color: '#1f2937' }}>{device.name}</td>
                    <td style={{ color: '#4b5563' }}>{device.os}</td>
                    <td>
                      {(() => {
                        const isOnline = device.last_seen && (new Date().getTime() - new Date(device.last_seen).getTime() < 5 * 60 * 1000);
                        return (
                          <span className={`status-badge ${isOnline ? 'compliant' : 'warning'}`}>
                            {isOnline ? 'online' : 'offline'}
                          </span>
                        );
                      })()}
                    </td>
                    <td>
                      <span className={`status-badge ${device.compliance_status === 'compliant' ? 'compliant' : 'error'}`}>
                        {device.compliance_status}
                      </span>
                    </td>
                    <td style={{ color: '#6b7280' }}>
                      {device.last_seen ? new Date(device.last_seen).toLocaleString() : 'Never'}
                    </td>
                    <td>
                      <button className="quick-action-btn" style={{ fontSize: '12px', padding: '4px 8px' }}>
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MacOSManagement;
